import API from './api';
import OPTS from './options';
import { writable, derived, get } from 'svelte/store';
import { newTag, newTerm, SEARCH_TYPES } from './typefactories';
import { storeData, getStoredData } from './storage';
import { buildQuery, groupTerms, removeIf } from "./utils";

function newQueryStore() {
    // read data from local storage if any
    const storedData = getStoredData();

    // create a writable store with initial state from local storage if any or a new initial state
    const { subscribe, update } = writable(storedData ? storedData : {
        search_terms: OPTS.starting_query ? [newTerm(OPTS.starting_query)] : [],
        page: 1,
        sort: null
    });

    // subscribe to the store to write changes to local storage
    subscribe(storeData);

    // return the interface used to comunicate with this store
    return {
        subscribe,
        add(values) {
            // filter the values to remove duplicates
            const terms = get(this).search_terms;
            values = (Array.isArray(values) ? values : [values])
                .filter(it => !terms.some(t => t.id === it.id));

            // do not update if there are no new values
            if (!values.length)
                return;

            // find elements to remove
            const toRemove = values.filter(it => it.switch).map(it => {
                const s = it.switch;
                delete it.switch;
                return s;
            });
            update($q => ({
                ...$q,
                search_terms: [
                    // for types RANGE_YEAR_FILTER and FUND_FILTER there must be one single value
                    ...(removeIf(values, $q.search_terms, [
                        (it) => it.type === SEARCH_TYPES.RANGE_YEAR_FILTER,
                        (it) => it.type === SEARCH_TYPES.FUND_FILTER
                    ])).filter(it => !toRemove.includes(it.id)),
                    ...values
                ],
                page: 1
            }));
        },
        remove(ids) {
            ids = Array.isArray(ids) ? ids : [ids];
            update($q => ({...$q, search_terms: $q.search_terms.filter(it => !ids.includes(it.id)), page: 1}));
        },
        setPage(page) {
            update($q => ({...$q, page: page}));
        },
        clear() {
            update($q => ({
                search_terms: OPTS.selected_fund === 'all' ? [] : $q.search_terms.filter(it => it.type === SEARCH_TYPES.FUND_FILTER),
                page: 1,
                sort: null}));
        },
        sort(field, order) {
            update($q => ({...$q, sort: field === 'relevance' ? null : { field: field, order: order }}));
        }
    }
}

const query = newQueryStore();

// derived store with all search terms (free terms, advanced terms and filters)
const searchTags = derived(query, $query => [
    ...$query.search_terms
        .filter(it => it.type !== SEARCH_TYPES.RANGE_YEAR_FILTER && it.type !== SEARCH_TYPES.FUND_FILTER && it.type !== SEARCH_TYPES.FILTER)
        .map(it => newTag(it.id, it.label || it.value))
], []);

// asynchronous derived store which makes a query and exposes the search results
const searchResult = derived(query, ($query, set) => {
    // is this the latest request made?
    let isLatest = true;

    const structQuery = groupTerms($query.search_terms);

    // builds a valid query and calls the search API
    API.search({
        query: buildQuery(structQuery[SEARCH_TYPES.TERM], structQuery[SEARCH_TYPES.ADV_TERM]),
        filters: [
            ...OPTS.global_filters,
            ...structQuery[SEARCH_TYPES.FILTER].map(it => it.fq),
            ...structQuery[SEARCH_TYPES.RANGE_YEAR_FILTER].map(it => it.fq),
            ...structQuery[SEARCH_TYPES.FUND_FILTER].filter(it => it.fq !== '').map(it => it.fq)
        ],
        paging: {
            limit: OPTS.page_size,
            start: ($query.page - 1) * OPTS.page_size
        },
        facets: OPTS.facets.map(it => it.field).filter(it => it),
        sort: $query.sort
    }).then(data => isLatest && set(data));

    // invalidate the current request if a new one arrives or all components unsubscribe
    return () => isLatest = false;
}, {total: 0, facets:{}, docs:[], yearMin: 0, yearMax: 0});

export { query, searchTags, searchResult };
